import * as React from "react"

import {
  examRoleFromWorkspaceRole,
  LOGIN_SESSION_CHANGED_EVENT,
  workspaceRoleFromExamRole,
  type ExamUserRole,
} from "@/lib/exam-role-from-session"
import { getLoginSession, setLoginSession } from "@/lib/login-session"

export type UserRole = ExamUserRole

function readExamRoleFromSession(): UserRole {
  return examRoleFromWorkspaceRole(getLoginSession().role)
}

function subscribeToLoginSession(onStoreChange: () => void) {
  if (typeof window === "undefined") return () => {}
  const handler = () => onStoreChange()
  window.addEventListener(LOGIN_SESSION_CHANGED_EVENT, handler)
  return () => window.removeEventListener(LOGIN_SESSION_CHANGED_EVENT, handler)
}

// Mock provider kept for App shell compatibility.
export function ExamRoleProvider({ children }: { children: React.ReactNode }) {
  return <>{children}</>
}

/** Exam Management RBAC — follows workspace sign-in role, not a separate default. */
export function useExamRole() {
  const role = React.useSyncExternalStore(
    subscribeToLoginSession,
    readExamRoleFromSession,
    () => "Admin" satisfies UserRole,
  )

  const setRole = React.useCallback((newRole: UserRole) => {
    const session = getLoginSession()
    setLoginSession({
      ...session,
      role: workspaceRoleFromExamRole(newRole),
    })
  }, [])

  return { role, setRole }
}
